import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useTranslator } from 'tradera/lang/translate';
import { webApiClient } from 'tradera/utils/http';
import { setIsInternationalWacko } from 'tradera/state/views/view-item/reducer';
import { selectPreferredLanguage } from 'tradera/state/language/selectors';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import styles from './card-verification.module.scss';
import { Button } from '../atoms/button/button';

const states = {
    START: 'START',
    LOADING: 'LOADING',
    CARD_VERIFICATION_MODAL: 'CARD_VERIFICATION_MODAL',
    ERROR: 'ERROR',
    NOT_VERIFIED: 'NOT_VERIFIED'
};

export const CardVerification = ({
    onCardVerified,
    setIsHidden,
    setSelectedIdentificationIsLoading,
    signal
}) => {
    const preferredLanguage = useSelector(selectPreferredLanguage);
    const memberEmail = useSelector((state) => state.member.memberEmail);
    const [state, setState] = useState(states.START);
    const { t } = useTranslator();
    const submitButtonRef = useRef();
    const dispatch = useDispatch();

    useEffect(() => {
        if (state !== states.START) {
            return;
        }

        GtmService.trackAction('VIP', 'Card Verification', 'Start');

        const onSuccess = (nonce) => {
            setState(states.LOADING);
            webApiClient
                .post(
                    '/shopping/payment/verifybraintreecardnonce',
                    { nonce },
                    { signal: signal }
                )
                .then(({ data }) => {
                    setSelectedIdentificationIsLoading(false);

                    if (data.isVerified) {
                        onCardVerified();
                        GtmService.trackAction(
                            'VIP',
                            'Card Verification',
                            'Verified'
                        );
                        dispatch(setIsInternationalWacko(false));
                        return;
                    }
                    GtmService.trackAction(
                        'VIP',
                        'Card Verification',
                        'NotVerified'
                    );
                    setIsHidden(false);
                    setState(states.NOT_VERIFIED);
                    return;
                })
                .catch((ex) => {
                    if (axios.isCancel(ex)) {
                        return;
                    }

                    GtmService.trackAction(
                        'VIP',
                        'Card Verification',
                        'Server error'
                    );
                    setState(states.ERROR);
                    setSelectedIdentificationIsLoading(false);
                });
        };

        webApiClient
            .get('/shopping/payment/braintreeclienttokenforverification', {
                signal: signal
            })
            .then(async ({ data }) => {
                setSelectedIdentificationIsLoading(false);
                setState(states.CARD_VERIFICATION_MODAL);
                const options = {
                    authorization: data.clientToken,
                    container: '#dropin-container',
                    locale: preferredLanguage.languageCodeIso2,
                    threeDSecure: true
                };
                const dropin = await import(
                    /* webpackChunkName: "braintree-web-drop-in" */ 'braintree-web-drop-in'
                );
                dropin.create(options, (error, instance) => {
                    if (error) {
                        return;
                    }
                    submitButtonRef.current.addEventListener('click', () => {
                        setIsHidden(true);
                        try {
                            instance.requestPaymentMethod(
                                {
                                    threeDSecure: {
                                        amount: '1',
                                        email: memberEmail
                                    }
                                },
                                (requestPaymentMethodErr, payload) => {
                                    if (requestPaymentMethodErr) {
                                        setIsHidden(false);
                                        setState(states.NOT_VERIFIED);
                                        const error =
                                            requestPaymentMethodErr._braintreeWebError ||
                                            {};

                                        GtmService.trackAction(
                                            'VIP',
                                            'Card Verification',
                                            `${error.type}:${error.code}`
                                        );

                                        return;
                                    }

                                    if (!payload || !payload.nonce) {
                                        setIsHidden(false);
                                        setState(states.NOT_VERIFIED);

                                        if (payload) {
                                            const threeDSecureValidationFailed =
                                                payload.liabilityShiftPossible &&
                                                !payload.liabilityShifted;

                                            GtmService.trackAction(
                                                'VIP',
                                                'Card Verification',
                                                threeDSecureValidationFailed
                                                    ? 'ThreeDSecure failed'
                                                    : 'Card invalid'
                                            );
                                        }
                                        return;
                                    }

                                    onSuccess(payload.nonce);
                                }
                            );
                        } catch (e) {
                            setSelectedIdentificationIsLoading(false);
                            setIsHidden(false);
                            setState(states.NOT_VERIFIED);
                            GtmService.trackAction(
                                'VIP',
                                'Card Verification',
                                'requestPaymentMethod failed'
                            );
                            throw e;
                        }
                    });
                });
                return;
            })
            .catch((ex) => {
                if (axios.isCancel(ex)) {
                    return;
                }

                setSelectedIdentificationIsLoading(false);
                setState(states.ERROR);
            });
        setState(states.LOADING);
    }, [
        onCardVerified,
        dispatch,
        state,
        preferredLanguage,
        memberEmail,
        setIsHidden,
        setSelectedIdentificationIsLoading,
        signal
    ]);

    const restart = () => {
        GtmService.trackAction('VIP', 'Card Verification', 'Restart');
        setState(states.START);
    };

    if (state == states.LOADING || state == states.START) {
        return null;
    }

    return (
        <div className={`m-3 ${styles.cardVerificationContainer}`}>
            {state === states.CARD_VERIFICATION_MODAL && (
                <>
                    <div id="dropin-container" />

                    <p className="my-2 size-oslo">
                        {t(
                            'internationalwacko_cardverificationmodal_disclaimer'
                        )}
                    </p>

                    <Button theme="primary" fluid ref={submitButtonRef}>
                        {t(
                            'internationalwacko_cardverificationmodal_verifyCard'
                        )}
                    </Button>
                </>
            )}
            {state === states.ERROR && (
                <p className="text-center text-danger p-5">
                    {t('internationalwacko_cardverificationmodal_unavailable')}
                </p>
            )}
            {state === states.NOT_VERIFIED && (
                <div className="text-center p-5">
                    <h4 className="my-4">
                        {t(
                            'internationalwacko_cardverificationmodal_failedVerification'
                        )}
                    </h4>
                    <p className="mb-2">
                        {t(
                            'internationalwacko_cardverificationmodal_failedVerification_info'
                        )}
                    </p>
                    <Button theme="primary" fluid onClick={restart}>
                        {t('internationalwacko_cardverificationmodal_tryAgain')}
                    </Button>
                </div>
            )}
        </div>
    );
};
